"use client";

import { useEffect, useRef } from "react";

/**
 * Fixed "cosmic" backdrop for the homepage: nebula gradients + grid (CSS),
 * a canvas starfield with occasional shooting stars, and a cursor-following
 * glow. Ported 1:1 from the prototype's index.js (parts 1 & 2).
 * Respects prefers-reduced-motion (skips the animation loop).
 */
export function CosmosBackground() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const glowRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    let raf = 0;
    const cleanups: Array<() => void> = [];

    // ---- starfield ----
    const cvs = canvasRef.current;
    if (cvs && !reduce) {
      const ctx = cvs.getContext("2d");
      if (ctx) {
        let stars: Array<{ x: number; y: number; z: number; tw: number; color: string }> = [];
        const shooters: Array<{ x: number; y: number; vx: number; vy: number; life: number; tail: number }> = [];
        let W = 0;
        let H = 0;
        const dpr = Math.min(window.devicePixelRatio || 1, 2);

        const resize = () => {
          W = cvs.width = window.innerWidth * dpr;
          H = cvs.height = window.innerHeight * dpr;
          cvs.style.width = window.innerWidth + "px";
          cvs.style.height = window.innerHeight + "px";
          stars = [];
          const count = Math.floor((window.innerWidth * window.innerHeight) / 5500);
          for (let i = 0; i < count; i++) {
            stars.push({
              x: Math.random() * W,
              y: Math.random() * H,
              z: Math.random() * 0.8 + 0.2,
              tw: Math.random() * Math.PI * 2,
              color:
                Math.random() > 0.85 ? "#4DE8FF" : Math.random() > 0.7 ? "#B5A7FF" : "#ffffff",
            });
          }
        };
        resize();
        window.addEventListener("resize", resize);
        cleanups.push(() => window.removeEventListener("resize", resize));

        const shootingStar = () => {
          shooters.push({
            x: Math.random() * W,
            y: Math.random() * H * 0.4,
            vx: (8 + Math.random() * 6) * dpr,
            vy: (4 + Math.random() * 3) * dpr,
            life: 1,
            tail: 80 * dpr,
          });
        };
        const interval = window.setInterval(() => {
          if (Math.random() > 0.6) shootingStar();
        }, 2400);
        cleanups.push(() => window.clearInterval(interval));

        const loop = () => {
          ctx.clearRect(0, 0, W, H);
          for (const s of stars) {
            s.tw += 0.02;
            const a = 0.4 + Math.sin(s.tw) * 0.3 + s.z * 0.3;
            ctx.fillStyle = s.color;
            ctx.globalAlpha = a;
            ctx.beginPath();
            ctx.arc(s.x, s.y, s.z * 1.4 * dpr, 0, Math.PI * 2);
            ctx.fill();
          }
          ctx.globalAlpha = 1;
          for (let i = shooters.length - 1; i >= 0; i--) {
            const sh = shooters[i];
            sh.x += sh.vx;
            sh.y += sh.vy;
            sh.life -= 0.008;
            if (sh.life <= 0 || sh.x > W || sh.y > H) {
              shooters.splice(i, 1);
              continue;
            }
            const grad = ctx.createLinearGradient(sh.x, sh.y, sh.x - sh.vx * 10, sh.y - sh.vy * 10);
            grad.addColorStop(0, `rgba(77,232,255,${sh.life})`);
            grad.addColorStop(1, "rgba(77,232,255,0)");
            ctx.strokeStyle = grad;
            ctx.lineWidth = 2 * dpr;
            ctx.beginPath();
            ctx.moveTo(sh.x, sh.y);
            ctx.lineTo(sh.x - sh.vx * 10, sh.y - sh.vy * 10);
            ctx.stroke();
          }
          raf = requestAnimationFrame(loop);
        };
        raf = requestAnimationFrame(loop);
        cleanups.push(() => cancelAnimationFrame(raf));

        // Don't burn CPU/battery when the tab is in the background.
        const onVisibility = () => {
          cancelAnimationFrame(raf);
          if (!document.hidden) raf = requestAnimationFrame(loop);
        };
        document.addEventListener("visibilitychange", onVisibility);
        cleanups.push(() => document.removeEventListener("visibilitychange", onVisibility));
      }
    }

    // ---- cursor glow ----
    const glow = glowRef.current;
    if (glow) {
      if (window.matchMedia("(pointer:fine)").matches && !reduce) {
        let mx = window.innerWidth / 2;
        let my = window.innerHeight / 2;
        let cx = mx;
        let cy = my;
        let trailRaf = 0;
        const onMove = (e: MouseEvent) => {
          mx = e.clientX;
          my = e.clientY;
        };
        window.addEventListener("mousemove", onMove);
        const trail = () => {
          cx += (mx - cx) * 0.12;
          cy += (my - cy) * 0.12;
          glow.style.transform = `translate(${cx}px, ${cy}px) translate(-50%, -50%)`;
          trailRaf = requestAnimationFrame(trail);
        };
        trailRaf = requestAnimationFrame(trail);
        const onGlowVisibility = () => {
          cancelAnimationFrame(trailRaf);
          if (!document.hidden) trailRaf = requestAnimationFrame(trail);
        };
        document.addEventListener("visibilitychange", onGlowVisibility);
        cleanups.push(() => {
          window.removeEventListener("mousemove", onMove);
          document.removeEventListener("visibilitychange", onGlowVisibility);
          cancelAnimationFrame(trailRaf);
        });
      } else {
        glow.style.display = "none";
      }
    }

    return () => cleanups.forEach((fn) => fn());
  }, []);

  return (
    <>
      <div className="cosmos" aria-hidden>
        <canvas className="cosmos__stars" id="starfield" ref={canvasRef} />
        <div className="cosmos__grid" />
        <div className="cosmos__nebula cosmos__nebula--a" />
        <div className="cosmos__nebula cosmos__nebula--b" />
        <div className="cosmos__nebula cosmos__nebula--c" />
      </div>
      <div className="cursor-glow" aria-hidden ref={glowRef} />
    </>
  );
}
